/**
 * Ted #26 (#987) — "Confirm this reservation only" / "Confirm all N pending",
 * with every reservation reviewable before the batch applies.
 *
 * Ted Gualtier, 2026-09-15 (verbatim): *"Before confirming all, staff should
 * be able to quickly see the individual reservations, including any different
 * services, add-ons, notes, dates, or times, so an unusual reservation is not
 * accidentally approved as part of the batch."* and the principle the whole
 * item rests on: *"reduce duplicate work, but never remove staff control."*
 *
 * Which is why this is a LIST, not a count. Every row is ticked by default —
 * the common case is "yes, all five" and that must be one more tap — and
 * every row can be unticked, so the odd one out is handled separately instead
 * of being swept into the batch.
 *
 * DESIGN (docs/DESIGN-PHILOSOPHY.md)
 * - Counter Test: the dialog opens only when there IS a choice to make. One
 *   pending reservation stays one tap and never sees this screen.
 * - Principle 2: exactly one filled-primary button ("Confirm all N pending");
 *   "this one only" is the calm alternative, "Not now" the way out.
 * - Principle 5: a refusal is reported as the situation ("already checked
 *   in"), and the outcome lands on the page's banner — never only a toast.
 * - Principle 7: rows are ≥44px, the whole row is the checkbox's label.
 * - Five states: default · loading (skeleton rows) · empty · error (what
 *   happened + Retry) · busy (the apply is in flight).
 * - Tokens only; no raw hex, no ad-hoc px.
 */
import { useEffect, useMemo, useRef } from 'react';
import { todayIsoDate } from '../../lib/api.js';
import {
  formatReservationDate,
  isPastReservation,
  joinDogNames,
  type PendingReservation,
} from './confirm-pending.js';
import './confirm-pending-dialog.css';

export interface ConfirmPendingDialogProps {
  /** The reservation the staffer clicked Confirm on. Always present. */
  anchorId: string;
  customerName: string | null;
  /** Oldest-first, anchor included. Empty while `loading`. */
  items: PendingReservation[];
  selected: ReadonlySet<string>;
  onToggle: (bookingId: string) => void;
  /** The wider pending lookup is still in flight. */
  loading: boolean;
  /** The wider pending lookup failed — the desk can still confirm the one. */
  loadError: string | null;
  onRetryLoad: () => void;
  notifyCustomer: boolean;
  onNotifyChange: (next: boolean) => void;
  /**
   * LG (board Contracts §3) — the manager's reason for confirming the HELD
   * rows in this batch. Rendered only when a ticked row is held; the server
   * refuses a held confirm without it, and refuses it from a non-manager.
   */
  overrideReason?: string;
  onOverrideReasonChange?: (next: string) => void;
  /** Holds `bookings.review-hold:override:all` (owner / manager). */
  canOverrideHold?: boolean;
  /** An apply is in flight. */
  busy: boolean;
  actionError: string | null;
  onConfirmAnchorOnly: () => void;
  onConfirmSelected: () => void;
  onDismiss: () => void;
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function ConfirmPendingDialog(props: ConfirmPendingDialogProps): JSX.Element {
  const {
    anchorId,
    customerName,
    items,
    selected,
    onToggle,
    loading,
    loadError,
    onRetryLoad,
    notifyCustomer,
    onNotifyChange,
    overrideReason = '',
    onOverrideReasonChange = () => undefined,
    canOverrideHold = false,
    busy,
    actionError,
    onConfirmAnchorOnly,
    onConfirmSelected,
    onDismiss,
  } = props;

  const dismissRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    dismissRef.current?.focus();
  }, []);

  // Escape is the way out of any overlay; a confirm dialog that traps the
  // desk is a defect at the counter (Principle 7 / Counter Test).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onDismiss]);

  const selectedCount = useMemo(
    () => items.filter((i) => selected.has(i.bookingId)).length,
    [items, selected],
  );
  // LG — a ticked row that is held needs the manager's reason before the
  // primary can fire; a non-manager sees why and unticks it (the server
  // would 403 the override anyway — the #484 invariant, no dead button).
  const heldSelected = useMemo(
    () => items.filter((i) => selected.has(i.bookingId) && i.reviewHoldReason),
    [items, selected],
  );
  // Ted 10-09 (#915) — ticked visits that already happened. The server never
  // emails a confirmation about one, so the dialog says so instead of letting
  // the checkbox promise it.
  const pastSelected = useMemo(() => {
    const today = todayIsoDate();
    return items.filter((i) => selected.has(i.bookingId) && isPastReservation(i, today)).length;
  }, [items, selected]);
  const anchorHeld = items.some((i) => i.bookingId === anchorId && i.reviewHoldReason);
  const overrideReady = overrideReason.trim().length >= 3;
  const holdBlocksPrimary = heldSelected.length > 0 && (!canOverrideHold || !overrideReady);
  const holdBlocksAnchor = anchorHeld && (!canOverrideHold || !overrideReady);
  const allSelected = selectedCount === items.length && items.length > 0;
  const anchor = items.find((i) => i.bookingId === anchorId) ?? null;
  const anchorLabel = anchor ? joinDogNames(anchor.dogNames) || 'this reservation' : 'this reservation';

  // "Confirm all N pending" is Ted's wording. With a single reservation on
  // the list there is no "all" to speak of — and no "only" either, so that
  // button is not rendered at all rather than sitting there meaning nothing.
  const soleReservation = items.length === 1;
  const primaryLabel = soleReservation
    ? 'Confirm this reservation'
    : allSelected
      ? `Confirm all ${items.length} pending`
      : `Confirm ${selectedCount} selected`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-pending-title"
      data-testid="confirm-pending-dialog"
      className="ferro-blocking-dialog__overlay"
    >
      <div className="ferro-blocking-dialog__panel ferro-confirm-pending">
        <h2 id="confirm-pending-title">
          {loading
            ? 'Checking for other pending reservations…'
            : `${customerName ?? 'This client'} has ${items.length} pending reservation${
                items.length === 1 ? '' : 's'
              }`}
        </h2>
        <p className="ferro-confirm-pending__lede">
          Confirm just {anchorLabel}&apos;s visit, or review the list and
          confirm them together. Untick anything that needs a closer look — it
          stays pending.
        </p>

        {/* ── error: the wider lookup failed ─────────────────────────────── */}
        {loadError ? (
          <div
            className="ferro-banner ferro-banner--warning"
            role="alert"
            data-testid="confirm-pending-load-error"
          >
            <p>
              Couldn&apos;t load this client&apos;s other reservations, so only
              the one you tapped is listed. {loadError}
            </p>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onRetryLoad}
              disabled={busy}
              data-testid="confirm-pending-retry"
            >
              Try again
            </button>
          </div>
        ) : null}

        {/* ── loading: skeleton rows, never a blank panel ────────────────── */}
        {loading ? (
          <ul
            className="ferro-confirm-pending__list"
            data-testid="confirm-pending-skeleton"
            aria-hidden="true"
          >
            <li className="ferro-confirm-pending__skeleton" />
            <li className="ferro-confirm-pending__skeleton" />
          </ul>
        ) : null}

        {/* ── empty: nothing to review (defensive; the caller does not open
                the dialog for a lone reservation) ───────────────────────── */}
        {!loading && items.length === 0 ? (
          <p className="ferro-confirm-pending__empty" data-testid="confirm-pending-empty">
            There is nothing here to confirm — the reservation may have already
            been handled. Close this and refresh the list.
          </p>
        ) : null}

        {/* ── default: the reviewable list ───────────────────────────────── */}
        {!loading && items.length > 0 ? (
          <ul className="ferro-confirm-pending__list" data-testid="confirm-pending-list">
            {items.map((item) => {
              const checked = selected.has(item.bookingId);
              const dogs = joinDogNames(item.dogNames);
              return (
                <li
                  key={item.bookingId}
                  className={`ferro-confirm-pending__item${
                    item.bookingId === anchorId
                      ? ' ferro-confirm-pending__item--anchor'
                      : ''
                  }`}
                  data-testid={`confirm-pending-item-${item.bookingId}`}
                >
                  <label className="ferro-confirm-pending__row">
                    <input
                      type="checkbox"
                      className="ferro-confirm-pending__check"
                      checked={checked}
                      disabled={busy}
                      onChange={() => onToggle(item.bookingId)}
                      data-testid={`confirm-pending-check-${item.bookingId}`}
                      aria-label={`Include ${dogs || 'this reservation'} on ${formatReservationDate(item.dateISO)}`}
                    />
                    <span className="ferro-confirm-pending__body">
                      <span className="ferro-confirm-pending__head">
                        <span className="ferro-confirm-pending__dogs">
                          {dogs || 'Reservation'}
                        </span>
                        {item.bookingId === anchorId ? (
                          <span className="ferro-confirm-pending__tag">
                            the one you tapped
                          </span>
                        ) : null}
                      </span>
                      <span className="ferro-confirm-pending__meta">
                        {formatReservationDate(item.dateISO)}
                        {item.timeLabel ? ` · ${item.timeLabel}` : ''}
                        {` · ${item.serviceLabel}`}
                        {item.totalCents > 0 ? ` · ${money(item.totalCents)}` : ''}
                      </span>
                      {item.addOns.length > 0 ? (
                        <span
                          className="ferro-confirm-pending__addons"
                          data-testid={`confirm-pending-addons-${item.bookingId}`}
                        >
                          Add-ons: {item.addOns.join(', ')}
                        </span>
                      ) : null}
                      {item.notes ? (
                        <span
                          className="ferro-confirm-pending__notes"
                          data-testid={`confirm-pending-notes-${item.bookingId}`}
                        >
                          {item.notes}
                        </span>
                      ) : null}
                      {/* LG — the hold, on the row, in the server's words. */}
                      {item.reviewHoldReason ? (
                        <span
                          className="ferro-confirm-pending__hold"
                          role="status"
                          data-testid={`confirm-pending-hold-${item.bookingId}`}
                        >
                          <strong>Needs staff review:</strong> {item.reviewHoldReason}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        ) : null}

        {/* Ted #12 (#915) — the desk decides whether the client hears about
            it, on the batch exactly as on a single staff-created booking. */}
        <label className="ferro-confirm-pending__notify">
          <input
            type="checkbox"
            checked={notifyCustomer}
            disabled={busy}
            onChange={(e) => onNotifyChange(e.target.checked)}
            data-testid="confirm-pending-notify"
          />
          <span>Email the client a confirmation for each one</span>
        </label>
        {pastSelected > 0 ? (
          <p className="ferro-confirm-pending__past-note" data-testid="confirm-pending-past-note">
            {pastSelected === 1
              ? 'One ticked visit has already happened — its client will not be emailed either way.'
              : `${pastSelected} ticked visits have already happened — their clients will not be emailed either way.`}
          </p>
        ) : null}

        {/* LG (board Contracts §3) — a manager confirms a held row by saying
            why; the reason lands on the audit row. A non-manager sees the
            rule, not a field the server would refuse. */}
        {heldSelected.length > 0 ? (
          canOverrideHold ? (
            <label className="ferro-confirm-pending__override" data-testid="confirm-pending-override">
              <span className="ferro-confirm-pending__override-label">
                {heldSelected.length === 1
                  ? 'This reservation is held for review. Say why you are confirming it anyway:'
                  : `${heldSelected.length} ticked reservations are held for review. Say why you are confirming them anyway:`}
              </span>
              <input
                id="confirm-pending-override-reason"
                name="override_reason"
                type="text"
                className="ferro-field__input"
                value={overrideReason}
                maxLength={300}
                placeholder="e.g. separate yards today — Ted approved"
                disabled={busy}
                onChange={(e) => onOverrideReasonChange(e.target.value)}
                data-testid="confirm-pending-override-reason"
              />
            </label>
          ) : (
            <p
              className="ferro-confirm-pending__override-locked"
              role="status"
              data-testid="confirm-pending-override-locked"
            >
              Held reservations can only be confirmed by a manager or owner. Untick them to confirm the rest.
            </p>
          )
        ) : null}

        {actionError ? (
          <div
            className="ferro-confirm-pending__error"
            role="alert"
            data-testid="confirm-pending-action-error"
          >
            <p>{actionError}</p>
          </div>
        ) : null}

        <div className="ferro-blocking-dialog__actions ferro-confirm-pending__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--deprioritized"
            ref={dismissRef}
            onClick={onDismiss}
            disabled={busy}
            data-testid="confirm-pending-dismiss"
          >
            Not now
          </button>
          {soleReservation || loading ? null : (
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={onConfirmAnchorOnly}
              disabled={busy || holdBlocksAnchor}
              data-testid="confirm-pending-one"
            >
              {busy ? 'Confirming…' : 'Confirm this reservation only'}
            </button>
          )}
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={onConfirmSelected}
            disabled={busy || loading || selectedCount === 0 || holdBlocksPrimary}
            data-testid="confirm-pending-all"
          >
            {busy ? 'Confirming…' : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
